/**
 * Studentized range distribution: lower tail P(Q ≤ q).
 * Algorithm follows Hartley's form with Gauss–Legendre quadrature
 * (Copenhaver & Holland, J Stat Comput Simul 30:1–15, 1988;
 * quadrature nodes from Stroud & Secrest, 1966).
 * Parameterization matches R stats::ptukey(q, nmeans, df) with nranges = 1.
 */

const M_1_SQRT_2PI = 1 / Math.sqrt(2 * Math.PI);
const M_LN2 = Math.LN2;

/** Abramowitz–Stegun 7.1.26 */
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-ax * ax);
  return sign * y;
}

/** Standard normal lower tail Φ(x) */
function pnormLower(x: number): number {
  return 0.5 * (1 + erf(x / Math.SQRT2));
}

/** log Γ(x), Lanczos (same family as tTest.ts) */
function logGamma(z: number): number {
  const coefficients = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.3234287776531,
    -176.6150291621406, 12.507343278686905, -0.13857109526572012, 0.000009984369578019572,
    0.00000015056327351493116,
  ];
  const g = 7;
  if (z < 0.5) {
    return Math.log(Math.PI) - Math.log(Math.sin(Math.PI * z)) - logGamma(1 - z);
  }
  let x = coefficients[0]!;
  const denom = z - 1;
  for (let i = 1; i < coefficients.length; i += 1) {
    x += coefficients[i]! / (denom + i);
  }
  const tt = denom + g + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (denom + 0.5) * Math.log(tt) - tt + Math.log(x);
}

function lgammafn(u: number): number {
  return logGamma(u);
}

/**
 * wprob: Hartley form of the range integral (R nmath wprob).
 * @param w range argument
 * @param rr number of studentized ranges (R nranges; usually 1)
 * @param cc number of means / treatments (k)
 */
function wprob(w: number, rr: number, cc: number): number {
  const nleg = 12;
  const ihalf = 6;
  const C1 = -30;
  const C2 = -50;
  const C3 = 60;
  const bb = 8;
  const wlar = 3;
  const wincr1 = 2;
  const wincr2 = 3;
  const xleg = [
    0.981560634246719250690549090149, 0.904117256370474856678465866119,
    0.769902674194304687036893833213, 0.587317954286617447296702418941,
    0.367831498998180193752691536644, 0.125233408511468915472441369464,
  ];
  const aleg = [
    0.047175336386511827194615961485, 0.106939325995318430960254718194,
    0.160078328543346226334652529543, 0.20316742672306592174906445581,
    0.233492536538354808760849898925, 0.249147045813402785000562436043,
  ];

  const qsqz = w * 0.5;
  if (qsqz >= bb) {
    return 1;
  }

  let prW = 2 * pnormLower(qsqz) - 1;
  if (prW >= Math.exp(C2 / cc)) {
    prW = prW ** cc;
  } else {
    prW = 0;
  }

  const wincr = w > wlar ? wincr1 : wincr2;
  let blb = qsqz;
  const binc = (bb - qsqz) / wincr;
  let bub = blb + binc;
  let einsum = 0;

  const cc1 = cc - 1;
  for (let wi = 1; wi <= wincr; wi += 1) {
    let elsum = 0;
    const a = 0.5 * (bub + blb);
    const b = 0.5 * (bub - blb);

    for (let jj = 1; jj <= nleg; jj += 1) {
      let j: number;
      let xx: number;
      if (ihalf < jj) {
        j = nleg - jj + 1;
        xx = xleg[j - 1]!;
      } else {
        j = jj;
        xx = -xleg[j - 1]!;
      }
      const c = b * xx;
      const ac = a + c;
      const qexpo = ac * ac;
      if (qexpo > C3) {
        break;
      }
      const pplus = 2 * pnormLower(ac);
      const pminus = 2 * pnormLower(ac - w);
      let rinsum = pplus * 0.5 - pminus * 0.5;
      if (rinsum >= Math.exp(C1 / cc1)) {
        rinsum = aleg[j - 1]! * Math.exp(-0.5 * qexpo) * rinsum ** cc1;
        elsum += rinsum;
      }
    }
    elsum *= 2 * b * cc * M_1_SQRT_2PI;
    einsum += elsum;
    blb = bub;
    bub += binc;
  }

  prW += einsum;
  if (prW <= Math.exp(C1 / rr)) {
    return 0;
  }
  prW = prW ** rr;
  if (prW >= 1) {
    return 1;
  }
  return prW;
}

/**
 * R ptukey(q, rr, cc, df, lower_tail=TRUE) without log_p.
 * For one-way Tukey HSD use rr = 1, cc = k (number of groups).
 */
export function ptukeyLowerTail(q: number, rr: number, cc: number, df: number): number {
  const nlegq = 16;
  const ihalfq = 8;
  const eps1 = -30;
  const eps2 = 1e-14;
  const dhaf = 100;
  const dquar = 800;
  const deigh = 5000;
  const dlarg = 25000;
  const ulen1 = 1;
  const ulen2 = 0.5;
  const ulen3 = 0.25;
  const ulen4 = 0.125;
  const xlegq = [
    0.98940093499164993259615417345, 0.944575023073232576077988415535,
    0.865631202387831743880467897712, 0.755404408355003033895101194847,
    0.617876244402643748446671764049, 0.458016777657227386342419442984,
    0.28160355077925891323046050146, 0.095012509837637440185319335425,
  ];
  const alegq = [
    0.027152459411754094851780572456, 0.0622535239386478928628438369944,
    0.0951585116824927848099251076022, 0.124628971255533872052476282192,
    0.149595988816576732081501730547, 0.16915651939500253818931207903,
    0.182603415044923588866763667969, 0.189450610455068496285396723208,
  ];

  if (!Number.isFinite(q) || !Number.isFinite(rr) || !Number.isFinite(cc) || !Number.isFinite(df)) {
    return NaN;
  }
  if (q <= 0) {
    return 0;
  }
  if (df < 2 || rr < 1 || cc < 2) {
    return NaN;
  }
  if (q > 1e12) {
    return 1;
  }

  if (df > dlarg) {
    return wprob(q, rr, cc);
  }

  const f2 = df * 0.5;
  let f2lf = f2 * Math.log(df) - df * M_LN2 - lgammafn(f2);
  const f21 = f2 - 1;
  const ff4 = df * 0.25;
  let ulen: number;
  if (df <= dhaf) {
    ulen = ulen1;
  } else if (df <= dquar) {
    ulen = ulen2;
  } else if (df <= deigh) {
    ulen = ulen3;
  } else {
    ulen = ulen4;
  }

  f2lf += Math.log(ulen);
  let ans = 0;
  let lastOtsum = 0;

  for (let i = 1; i <= 50; i += 1) {
    let otsum = 0;
    const twa1 = (2 * i - 1) * ulen;

    for (let jj = 1; jj <= nlegq; jj += 1) {
      let j: number;
      let t1: number;
      let qsqz: number;

      if (ihalfq < jj) {
        j = jj - ihalfq - 1;
        t1 = f2lf + f21 * Math.log(twa1 + xlegq[j]! * ulen) - (xlegq[j]! * ulen + twa1) * ff4;
        qsqz = q * Math.sqrt(0.5 * (xlegq[j]! * ulen + twa1));
      } else {
        j = jj - 1;
        t1 = f2lf + f21 * Math.log(twa1 - xlegq[j]! * ulen) + (xlegq[j]! * ulen - twa1) * ff4;
        qsqz = q * Math.sqrt(0.5 * (-xlegq[j]! * ulen + twa1));
      }

      if (t1 >= eps1) {
        const wprb = wprob(qsqz, rr, cc);
        const rotsum = wprb * alegq[j]! * Math.exp(t1);
        otsum += rotsum;
      }
    }

    lastOtsum = otsum;
    if (i * ulen >= 1 && otsum <= eps2) {
      break;
    }
    ans += otsum;
  }

  if (lastOtsum > eps2) {
    // non-convergence: still return clamped value (R warns)
  }
  if (ans > 1) {
    return 1;
  }
  return ans;
}
